// @ts-check
/** Wholesale tracker (Stockist Outreach Tracker): partners, timeline, quotes, agreements, reminders. Super Admin only. */

import api from '../utils/apiClient';

const BASE = '/api/admin/wholesale';

const qs = (params) => {
  const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : '';
};

const adminWholesaleService = {
  // partners
  listPartners: (filters) => api.get(`${BASE}/partners${qs(filters)}`),
  getPartner: (id) => api.get(`${BASE}/partners/${id}`),
  createPartner: (data) => api.post(`${BASE}/partners`, data),
  updatePartner: (id, data) => api.put(`${BASE}/partners/${id}`, data),
  /** Refused with a plain reason when the stage needs evidence that is not logged yet. */
  changeStage: (id, stage, note) => api.post(`${BASE}/partners/${id}/stage`, { stage, note }),

  // contacts
  addContact: (partnerId, data) => api.post(`${BASE}/partners/${partnerId}/contacts`, data),
  updateContact: (id, data) => api.put(`${BASE}/contacts/${id}`, data),
  deleteContact: (id) => api.del(`${BASE}/contacts/${id}`),

  // timeline
  listTouchpoints: (partnerId, q) => api.get(`${BASE}/partners/${partnerId}/touchpoints${qs({ q })}`),
  /** Returns the touchpoint plus suggested_stage and suggested_follow_up, both for the user to confirm. */
  logTouchpoint: (partnerId, data) => api.post(`${BASE}/partners/${partnerId}/touchpoints`, data),
  updateTouchpoint: (id, data) => api.put(`${BASE}/touchpoints/${id}`, data),
  parseFollowUp: (text) => api.get(`${BASE}/parse-follow-up${qs({ text })}`),

  // quotes
  listQuotes: (partnerId) => api.get(`${BASE}/partners/${partnerId}/quotes`),
  addQuote: (partnerId, data) => api.post(`${BASE}/partners/${partnerId}/quotes`, data),
  updateQuote: (id, data) => api.put(`${BASE}/quotes/${id}`, data),
  /** With no data, clones the quote's terms as KAAY's counter and supersedes the original. */
  counterQuote: (id, data) => api.post(`${BASE}/quotes/${id}/counter`, data),
  acceptQuote: (id) => api.post(`${BASE}/quotes/${id}/accept`),
  rejectQuote: (id) => api.post(`${BASE}/quotes/${id}/reject`),
  ranking: () => api.get(`${BASE}/quotes/ranking`),

  // agreements and placements
  addAgreement: (partnerId, data) => api.post(`${BASE}/partners/${partnerId}/agreements`, data),
  updateAgreement: (id, data) => api.put(`${BASE}/agreements/${id}`, data),
  listPlacements: (partnerId) => api.get(`${BASE}/partners/${partnerId}/placements`),
  addPlacement: (agreementId, data) => api.post(`${BASE}/agreements/${agreementId}/placements`, data),

  // reminders and summary
  listReminders: () => api.get(`${BASE}/reminders`),
  markReminderDone: (id) => api.post(`${BASE}/reminders/${id}/done`),
  sendDigest: () => api.post(`${BASE}/reminders/send-digest`),
  summary: () => api.get(`${BASE}/summary`),

  // templates
  listTemplates: () => api.get(`${BASE}/templates`),
  renderTemplate: (partnerId, data) => api.post(`${BASE}/partners/${partnerId}/render`, data),

  /** Submits the three wholesale WhatsApp templates Meta does not have yet. Returns { results: [{name, outcome, status, detail}] }. */
  submitWhatsAppTemplates: () => api.post(`${BASE}/whatsapp-templates/submit`),

  // outreach queue
  listOutreach: () => api.get(`${BASE}/outreach`),
  /** Body: { subject, body, contact_id, from_email }; from_email is required for email. */
  sendOutreach: (partnerId, data) => api.post(`${BASE}/outreach/${partnerId}/send`, data),

  /**
   * Sends the day-4 nudge (kind 'nudge') or the day-10 final line (kind 'final'). Same body as sendOutreach.
   * The response is the follow-up item; after a final it carries suggest_park: true.
   */
  sendFollowup: (partnerId, kind, data) => api.post(`${BASE}/outreach/${partnerId}/followup/${kind}/send`, data),

  /** Emails the draft to the logged-in admin only. Body: { subject, body, from_email }. Returns { sent_to, subject }. */
  testSendOutreach: (partnerId, data) => api.post(`${BASE}/outreach/${partnerId}/test-send`, data),

  /** Logs a first message the operator sent by hand (manual partners only). Body: { channel, note }. Sends nothing. */
  markOutreachSent: (partnerId, data) => api.post(`${BASE}/outreach/${partnerId}/mark-sent`, data),

  /** Logs that the operator submitted the partner's web form or application by hand. Body: { note }. Sends nothing. */
  markOutreachSubmitted: (partnerId, data) => api.post(`${BASE}/outreach/${partnerId}/mark-submitted`, data),

  // Gmail reply capture (read-only mailbox connection)
  gmailStatus: () => api.get(`${BASE}/gmail/status`),
  /** Returns { auth_url }: send the browser there to approve read-only access. */
  gmailConnect: () => api.post(`${BASE}/gmail/connect`, {}),
  gmailDisconnect: () => api.post(`${BASE}/gmail/disconnect`, {}),

  // export (CSV blobs)
  exportPartners: (range) => api.download(`${BASE}/export/partners${qs(range)}`),
  exportQuotes: (range) => api.download(`${BASE}/export/quotes${qs(range)}`),
};

export default adminWholesaleService;
